/**
 * Row-level RBAC helper for tasks.
 *
 * Rule (2026-02):
 *   A tenant "user" (non-admin) can only:
 *     - mark done  (status change)
 *     - (re)assign someone
 *     - set / change due_at
 *   on tasks they OWN (created) or that are assigned to them.
 *   Super Admin + Ultra Admin bypass every guard.
 *
 * Server enforces the same in `PATCH /tasks/{id}`. We mirror it in
 * the UI so users don't hit 403 toasts.
 */

const ADMIN_ROLES = new Set(["super_admin", "ultra_admin", "platform_admin"]);

export function isTaskAdmin(user) {
  return !!user && ADMIN_ROLES.has(user.role);
}

/** All assignee ids of a task (primary + co-assignees), unique & ordered. */
export function taskAssigneeIds(task) {
  if (!task) return [];
  const ids = [];
  if (task.assignee_id) ids.push(task.assignee_id);
  (task.assignee_ids || []).forEach((id) => {
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
}

/** True when `userId` is one of the task's assignees (primary OR co). */
export function isAssignedToUser(task, userId) {
  return !!userId && taskAssigneeIds(task).includes(userId);
}

/** True when `user` is allowed to complete / assign / reschedule the
 *  given `task`. Falsy `user` = no permission (defensive). */
export function canEditProtectedTaskFields(user, task) {
  if (!user || !task) return false;
  if (isTaskAdmin(user)) return true;
  if (user.id === task.created_by) return true;
  if (isAssignedToUser(task, user.id)) return true;
  return false;
}
